import React from 'react';
import { Clock, Users, Award, Heart } from 'lucide-react';
import { useTranslation } from '../i18nContext';

export default function HistorySection() {
  const t = useTranslation();
  const h = t.history;

  return (
    <section className="bg-white py-24" data-testid="home-history">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-cmic-50 rounded-full px-5 py-2 mb-6">
            <Clock size={16} className="text-cmic-600" />
            <span className="text-sm font-semibold text-cmic-700 tracking-wide">{t.hero.since}</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-charcoal-800 mb-4">{h.title}</h2>
          <div className="w-16 h-1 bg-cmic-500 mx-auto rounded-full" />
        </div>

        <div className="rounded-2xl overflow-hidden mb-16 shadow-xl">
          <img src="/CMIC-COCOYOC-82-1024x350.jpg" alt={h.photoCaption} className="w-full h-64 sm:h-80 object-cover" />
        </div>

        {/* Timeline intro paragraphs */}
        <div className="relative pl-8 sm:pl-12 mb-12">
          <div className="absolute left-2 sm:left-3 top-2 bottom-2 w-px bg-cmic-200" aria-hidden />
          <div className="space-y-8">
            {h.introParagraphs.map((p, i) => (
              <div key={i} className="relative">
                <span className="absolute -left-7 sm:-left-9 top-2 w-3 h-3 rounded-full bg-cmic-500 ring-4 ring-cmic-100" aria-hidden />
                <p className="text-lg text-charcoal-600 leading-[1.85]">{p}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Commissions block */}
        <div className="bg-cmic-50 border border-cmic-100 rounded-2xl p-6 sm:p-8 mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Users size={20} className="text-cmic-600" />
            <p className="text-charcoal-800 font-semibold text-base sm:text-lg">{h.commissionsTitle}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {h.commissions.map((c, i) => (
              <div key={i} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-cmic-100">
                <span className="w-7 h-7 rounded-full bg-cmic-500 text-white text-sm font-semibold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                <span className="text-sm text-charcoal-700 leading-snug">{c}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Mid paragraphs */}
        <div className="space-y-6 mb-10">
          {h.midParagraphs.map((p, i) => (
            <p key={i} className="text-lg text-charcoal-600 leading-[1.85]">{p}</p>
          ))}
        </div>

        {/* Premio Malintzin highlight */}
        <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 sm:p-8 mb-10 flex gap-4">
          <div className="w-11 h-11 rounded-xl bg-cmic-500 flex items-center justify-center flex-shrink-0">
            <Award size={22} className="text-white" />
          </div>
          <p className="text-charcoal-700 leading-[1.85] text-[15px]">{h.malintzinHighlight}</p>
        </div>

        {/* Social paragraph */}
        <p className="text-lg text-charcoal-600 leading-[1.85] mb-10">{h.socialParagraph}</p>

        {/* Solidaridad highlight */}
        <div className="bg-cmic-50 border border-cmic-100 rounded-2xl p-6 sm:p-8 mb-10 flex gap-4">
          <div className="w-11 h-11 rounded-xl bg-cmic-500 flex items-center justify-center flex-shrink-0">
            <Heart size={22} className="text-white" />
          </div>
          <p className="text-charcoal-700 leading-[1.85] text-[15px]">{h.solidaridadHighlight}</p>
        </div>

        {/* Closing paragraphs */}
        <div className="space-y-6 mb-12">
          {h.closingParagraphs.map((p, i) => (
            <p key={i} className="text-lg text-charcoal-600 leading-[1.85]">{p}</p>
          ))}
        </div>

        {/* CTA Badge */}
        <div className="text-center">
          <div className="inline-flex items-center gap-3 bg-cmic-700 text-white rounded-full px-8 py-4 shadow-lg">
            <span className="text-3xl font-bold">40+</span>
            <span className="text-left text-sm leading-tight font-medium text-cmic-100">
              {t.locale === 'es' ? 'Años fortaleciendo la interpretación en México' : 'Years strengthening interpretation in Mexico'}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
